/**
 * Phase 3.87 — ListingPerformanceHeatmap
 *
 * Kompakte 4-Zellen-Heatmap pro Inserat in der "Meine Inserate"-Liste.
 * Zeigt B2C-Aufrufe, B2B-Aufrufe, Favoriten und Weiterempfehlungen relativ
 * zu den Top-Werten über die eigenen Inserate des Händlers.
 *
 * Phase 3.88 — wenn ein Inserat seit > 7 Tagen ohne jegliche Aktivität ist
 * und nicht TOP/verkauft, blenden wir einen dezenten Action-Tipp ein.
 */
import { Eye, Heart, Share2, ShieldCheck, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";

const STEPS = 5; // 0=cold, 4=hot
const COLD_STREAK_DAYS = 7;

function intensity(value, max) {
    if (!value || value <= 0) return 0;
    if (!max || max <= 0) return 0;
    // Logarithmic-ish mapping so a few hot items don't drown the rest.
    const ratio = Math.min(1, value / max);
    return Math.min(STEPS - 1, Math.max(1, Math.round(ratio * (STEPS - 1))));
}

// Tailwind color ladders per metric — bg gets darker as intensity grows.
const CELL_COLORS = {
    b2c: ["bg-gray-100 text-gray-400", "bg-blue-100 text-blue-700", "bg-blue-200 text-blue-800", "bg-blue-400 text-white", "bg-blue-600 text-white"],
    b2b: ["bg-gray-100 text-gray-400", "bg-emerald-100 text-emerald-700", "bg-emerald-200 text-emerald-800", "bg-emerald-400 text-white", "bg-emerald-600 text-white"],
    favs: ["bg-gray-100 text-gray-400", "bg-rose-100 text-rose-700", "bg-rose-200 text-rose-800", "bg-rose-400 text-white", "bg-rose-600 text-white"],
    refs: ["bg-gray-100 text-gray-400", "bg-amber-100 text-amber-800", "bg-amber-200 text-amber-900", "bg-amber-400 text-amber-950", "bg-amber-500 text-amber-950"],
};

function Cell({ metric, value, max, label, Icon }) {
    const lvl = intensity(value, max);
    const cls = CELL_COLORS[metric][lvl];
    return (
        <div
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold tabular-nums ${cls}`}
            title={`${label}: ${value || 0} (Max: ${max})`}
            data-testid={`heatmap-${metric}`}
        >
            <Icon className="w-3 h-3" strokeWidth={2.5} />
            <span>{value || 0}</span>
        </div>
    );
}

export default function ListingPerformanceHeatmap({ listing, maxes }) {
    const b2c = listing.views_b2c || 0;
    const b2b = listing.views_b2b || 0;
    const favs = listing.favorites_count || 0;
    const refs = listing.referrals_count || 0;
    const total = b2c + b2b + favs + refs;

    // Phase 3.88 — Cold-Streak-Detection: aktiv & älter als 7 Tage & keine Aktivität
    // & nicht TOP & nicht verkauft → freundlicher Action-Tipp.
    const createdAt = listing.created_at ? new Date(listing.created_at).getTime() : null;
    const ageDays = createdAt ? (Date.now() - createdAt) / (1000 * 60 * 60 * 24) : 0;
    const isColdStreak = (
        total === 0 &&
        ageDays >= COLD_STREAK_DAYS &&
        !listing.is_top &&
        !listing.sold &&
        (listing.status || "active") === "active"
    );

    // Don't render if everything is zero AND this is a brand-new listing
    if (total === 0) {
        if (isColdStreak) {
            return (
                <div
                    className="mt-1.5 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5"
                    data-testid="listing-cold-streak-tip"
                >
                    <Lightbulb className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" strokeWidth={2.4} />
                    <div className="text-[11px] text-amber-900 leading-snug">
                        <strong className="font-bold">Tipp:</strong> Seit {Math.floor(ageDays)} Tagen ohne Aufrufe. Foto oder Preis prüfen —{" "}
                        <Link
                            to={`/edit-listing/${listing.id}`}
                            className="font-bold underline decoration-2 underline-offset-2 hover:text-amber-700"
                            data-testid="cold-streak-edit-link"
                         target="_blank" rel="noopener noreferrer">
                            Inserat überarbeiten
                        </Link>{" "}
                        oder per <strong className="text-[#DC2626]">★ Boost</strong> sichtbarer machen?
                    </div>
                </div>
            );
        }
        return (
            <div className="mt-1.5 text-[10px] text-gray-400" data-testid="heatmap-empty">
                Noch keine Aktivität — bald sind hier B2C/B2B-Aufrufe, Favoriten &amp; Weiterempfehlungen sichtbar.
            </div>
        );
    }

    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid="listing-heatmap">
            <Cell metric="b2c" value={b2c} max={maxes.b2c} label="B2C-Aufrufe" Icon={Eye} />
            <Cell metric="b2b" value={b2b} max={maxes.b2b} label="B2B-Aufrufe" Icon={ShieldCheck} />
            <Cell metric="favs" value={favs} max={maxes.favs} label="Favoriten" Icon={Heart} />
            <Cell metric="refs" value={refs} max={maxes.refs} label="Weiterempfehlungen" Icon={Share2} />
        </div>
    );
}
